import {createAsyncThunk} from '@reduxjs/toolkit'
import {Auth0Error} from 'auth0-js'
import {CognitoHostedUIIdentityProvider} from '@aws-amplify/auth'
import {
  getLastLoginUrl,
  persistUserLastConnectionMethodOnCookie,
  setLastLoginUrl
} from '../business/persistData'

import {
  getAuthSDK,
  passwordlessLoginService,
  signInService,
  signUpService,
  answerCustomChallengeService,
  configureAmplify,
  getCurrentAuthenticatedUserService,
  updateUserAttributesService,
  putStorageService,
  getStorageService,
  socialLoginService,
  updateLastLoginMethodService,
  getRecaptchaToken,
  upgradeUserTier
} from '../services'

import {
  UserPhoneNumberType,
  MessageChannelsType,
  SendMessageParams
} from '../types/Passwordless'
import {AuthConnectionWithAudience, ClaimsType} from '../types/Auth'
import {
  CognitoSession,
  User,
  UserAttributes,
  CognitoError,
  CognitoErrorCodes
} from '../types/Cognito'

import {trackEvent} from '../../analytics/modules/actions'
import {logEvent, trackFullStoryEvent} from '../../analytics/modules/middleware'
import {Action, Category, Entity, Team} from '../../analytics/types/Events'
import {handleRecaptchaValidationError} from '../utils/errors'
import {USER_TIERS} from '../../offer/business/privateDeals'

export enum SocialConnections {
  Google = 'google-oauth2',
  Facebook = 'facebook'
}

// Temporary ENUM for Cognito rollout
export enum AuthToolProvider {
  Cognito = 'Cognito',
  Auth0 = 'Auth0'
}

export interface SendMessageSuccess {
  selectedChannel: MessageChannelsType
  userPhoneNumber: UserPhoneNumberType
  cognitoSession?: CognitoSession
}

export interface SendMessageError {
  error: string
  error_description: string
}

interface OtpLoginParams {
  phoneNumber: UserPhoneNumberType
  code: string
  authConnection: AuthConnectionWithAudience
  selectedChannel?: MessageChannelsType
}

export type GenericAuth0Error = Auth0Error['error_description']

interface AnswerCustomChallengeParams
  extends Omit<OtpLoginParams, 'authConnection'> {
  user: CognitoSession
}

/**
 * Trigger Cognito Login
 * @param params - should contain all user to start login process
 */
export const startLoginSignUp = createAsyncThunk<
  SendMessageSuccess,
  SendMessageParams,
  {rejectValue: CognitoError; fulfillWithValue: SendMessageSuccess}
>(
  'startLoginSignUp',
  async (params: SendMessageParams, {rejectWithValue, dispatch}) => {
    configureAmplify()
    const {phoneNumber, selectedChannel, token} = params

    try {
      await signUpService({phoneNumber, token})
      const recaptchaToken = await getRecaptchaToken()
      return {
        cognitoSession: await signInService({...params, token: recaptchaToken}),
        userPhoneNumber: phoneNumber,
        selectedChannel
      }
    } catch (error) {
      try {
        const {code} = error as CognitoError
        if (code === CognitoErrorCodes.UsernameExistsException) {
          return {
            cognitoSession: await signInService(params),
            userPhoneNumber: phoneNumber,
            selectedChannel
          }
        }

        console.error('Cognito: startLoginSignUp (signup->signin):', error)

        if (code === CognitoErrorCodes.UserLambdaValidationException) {
          handleRecaptchaValidationError(error, dispatch)
        }

        return rejectWithValue(error as CognitoError)
      } catch (error) {
        console.error('Cognito: startLoginSignUp (signin):', error)
        const {code} = error as CognitoError

        if (code === CognitoErrorCodes.UserLambdaValidationException) {
          handleRecaptchaValidationError(error, dispatch)
        }

        return rejectWithValue(error as CognitoError)
      }
    }
  }
)

/**
 * Send users login token from provided info
 * @param params - should contain all user info provided to dispatch message
 */
export const sendMessage = createAsyncThunk<
  SendMessageSuccess,
  SendMessageParams,
  {rejectValue: SendMessageError}
>(
  'sendMessage',
  async (params: SendMessageParams, {rejectWithValue, dispatch}) => {
    const {phoneNumber, selectedChannel} = params

    try {
      await passwordlessLoginService({...params})

      return {
        selectedChannel,
        userPhoneNumber: phoneNumber
      }
    } catch (error) {
      const {response} = error

      if (response?.data?.type === 'Unauthorized') {
        const {
          data: {details}
        } = response

        logEvent('PasswordlessCaptchaError', details)
        trackFullStoryEvent('PasswordlessCaptchaError', details)

        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.PasswordlessSend,
            action: Action.Errored,
            payload: {
              score: details?.score
            },
            team: Team.Retention
          })
        )
      }

      return rejectWithValue(response.data)
    }
  }
)

/**
 * Authorize users via auth0 passwordless
 */
export const makeOtpLogin = createAsyncThunk<
  unknown,
  OtpLoginParams,
  {rejectValue: GenericAuth0Error}
>('makeOtpLogin', async (params, {rejectWithValue}) => {
  const {phoneNumber, code, authConnection, selectedChannel} = params

  setLastLoginUrl(window.location.href)
  persistUserLastConnectionMethodOnCookie(selectedChannel)

  return new Promise((resolve, reject) => {
    getAuthSDK(authConnection).passwordlessLogin(
      {
        connection: 'sms',
        phoneNumber,
        verificationCode: code
      },
      (error, response) => {
        if (error) {
          return reject(rejectWithValue(error.error_description))
        }

        return resolve(response)
      }
    )
  })
})

/**
 * Authorize users via auth0 sdk
 */
interface makeSocialLoginArgs {
  connection: SocialConnections
  authConnection: AuthConnectionWithAudience
  authToolProvider: AuthToolProvider
}

export const makeSocialLogin = createAsyncThunk<
  unknown,
  makeSocialLoginArgs,
  {rejectValue: GenericAuth0Error}
>('makeSocialLogin', async ({connection, authConnection, authToolProvider}) => {
  setLastLoginUrl(window.location.href)
  persistUserLastConnectionMethodOnCookie(connection)

  if (authToolProvider === AuthToolProvider.Cognito) {
    configureAmplify()

    const connectionMapping = {
      [SocialConnections.Google]: CognitoHostedUIIdentityProvider.Google,
      [SocialConnections.Facebook]: CognitoHostedUIIdentityProvider.Facebook
    }

    return socialLoginService(connectionMapping[connection])
  }

  return new Promise(() => {
    getAuthSDK(authConnection).authorize({connection})
  })
})

/**
 * Parse users token from auth0 sdk
 */
export const parseUserTokenFromCallbackUrl = createAsyncThunk<
  ClaimsType,
  AuthConnectionWithAudience,
  {rejectValue: GenericAuth0Error}
>(
  'parseUserTokenFromCallbackUrl',
  async (authConnection, {rejectWithValue}) => {
    return new Promise((resolve, reject) => {
      getAuthSDK(authConnection).parseHash(async (error, response) => {
        if (error) {
          return reject(rejectWithValue(error.error_description))
        }

        return resolve(response?.idTokenPayload)
      })
    })
  }
)

/**
 * Redirect users from callback login previous page
 */
export const redirectUsersFromCallbackUrl = createAsyncThunk<void>(
  'redirectUsersFromCallbackUrl',
  async () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    updateLastLoginMethodService('social')

    const lastLoginUrl = getLastLoginUrl()
    const url = new URL(lastLoginUrl)
    /* eslint-disable fp/no-mutation */
    window.location.href = url.pathname + url.search
    /* eslint-enable fp/no-mutation */
  }
)

/**
 * Answer Cognito Custom Challenge
 */
export const answerCustomChallenge = createAsyncThunk<
  User,
  AnswerCustomChallengeParams,
  {rejectValue: unknown}
>(
  'answerCustomChallenge',
  async ({user, code, selectedChannel}, {rejectWithValue}) => {
    try {
      const response = await answerCustomChallengeService(user, code)

      if (!response) {
        return rejectWithValue('Wrong phone number or verification code.')
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      updateLastLoginMethodService(selectedChannel)
      return response
    } catch (error) {
      console.error('Cognito: answerCustomChallengeService:', error)

      return rejectWithValue({error, user})
    }
  }
)

/**
 * Updates users' Cognito attributes
 */
export const updateUserAttributes = createAsyncThunk<
  UserAttributes,
  UserAttributes,
  {rejectValue: unknown}
>('updateUserAttributes', async (attributes, {rejectWithValue}) => {
  try {
    const user = await getCurrentAuthenticatedUserService()
    const response = await updateUserAttributesService(user, attributes)
    if (response === 'SUCCESS') {
      return attributes
    }

    return rejectWithValue(response)
  } catch (error) {
    return rejectWithValue(error)
  }
})

/**
 * Upgrades users to plus
 */
export const upgradeUserToPlus = createAsyncThunk<
  unknown,
  void,
  {
    rejectValue: {
      error: unknown
    }
  }
>('upgradeUserToPlus', async (_, {rejectWithValue, fulfillWithValue}) => {
  try {
    const user = await getCurrentAuthenticatedUserService()
    const response = await upgradeUserTier(user, USER_TIERS.plus)

    return fulfillWithValue(response)
  } catch (error) {
    return rejectWithValue(error)
  }
})

/**
 * Upload and update user profile picture
 */
export const uploadUserPicture = createAsyncThunk<
  UserAttributes,
  File,
  {rejectValue: unknown}
>('uploadUserPicture', async (file, {rejectWithValue, dispatch}) => {
  try {
    const user = await getCurrentAuthenticatedUserService()
    const upload = await putStorageService(user.attributes.sub, file, {
      level: 'protected',
      contentType: file.type
    })

    if (upload.key) {
      const pictureAttribute = {
        picture: upload.key
      }
      const updatePicture = await updateUserAttributesService(
        user,
        pictureAttribute
      )
      if (updatePicture === 'SUCCESS') {
        await dispatch(updateProfilePictureUrl(upload.key))
        return {...user.attributes, ...pictureAttribute}
      }
    }

    return rejectWithValue(undefined)
  } catch (error) {
    console.log('Error uploading file or updating user attribute:', error)
    return rejectWithValue(error)
  }
})

/**
 * Get user profile picture from S3 profile pictures bucket (by using Amplify `Storage.get`)
 */
export const updateProfilePictureUrl = createAsyncThunk<string, string>(
  'updateProfilePictureUrl',
  async key => {
    const oneHour = 3600
    return getStorageService(key, {
      level: 'protected',
      expires: oneHour
    })
  }
)
